/**
 * +quests: side-quest ops (docs/design/zones.md "Side quests"), run as intents (questAccept,
 * questTurnIn, questAbandon) offline in the browser and online on the server, which clamps them:
 *  - accept: the quest exists, the side quests are open (main quest past the Elder), the hero is at
 *    its minLevel, its chain predecessor is handed in, it is not taken or done, the log has room,
 *    and the hero's saved position is within QUEST_REACH_YD of the giver on the giver's map;
 *  - turn-in: it is active with enough kills / items (talk quests: at once), and the hero stands at
 *    the turn-in NPC; pays its XP (less when outgrown: sideQuestXp), gold and, for quests that
 *    say so, a gear piece rolled on the profile seed (server-side online: the intent rolls);
 *  - abandon: drops an active quest and its progress.
 * Kill progress rides on `fieldKills` (field.ts → sideQuestKills), the same budgeted kill events as
 * the bounties.
 */
import { type SideQuestDef, getContentBundle, sideQuestById } from '@keystone/content';
import { BASE_ILVL, makeItem } from './items';
import { CLASS_STAT_PROFILES } from './data';
import { profileRng, wearIfBetter } from './profile';
import { type FieldOp, fieldOpGrant, fieldOpSettle, fieldOpStart } from './field';
import { QUEST_LOG_MAX, QUEST_REACH_YD, emptySideQuests, questTurnInNpc, sideQuestStatus, sideQuestXp } from './sidequestState';
import type { HeroCharacter, Item, ItemSlot, Profile } from './types';
import { ITEM_SLOTS } from './types';

/** What a turn-in paid (the dialog's reward line and the banner). */
export interface SideQuestPaid {
  id: string;
  title: string;
  xp: number;
  gold: number;
  /** The quest was grey (outgrown): the XP is reduced. */
  grey: boolean;
}

const fail = (reason: string): { ok: false; reason: string } => ({ ok: false, reason });

/** The map an NPC stands on and where (null = unknown NPC). */
function npcPlace(npcId: string): { mapId: string; pos: { x: number; y: number } } | null {
  for (const d of getContentBundle().dungeons) {
    const n = d.field?.npcs?.find((x) => x.id === npcId);
    if (n) return { mapId: d.id, pos: n.pos };
  }
  return null;
}

/** The hero's saved position is next to `npcId` (same map, within QUEST_REACH_YD). */
export function atQuestNpc(hero: Pick<HeroCharacter, 'mapId' | 'pos'>, npcId: string): boolean {
  const at = npcPlace(npcId);
  if (!at || !hero.pos || hero.mapId !== at.mapId) return false;
  return Math.hypot(hero.pos.x - at.pos.x, hero.pos.y - at.pos.y) <= QUEST_REACH_YD;
}

const levelOf = (p: { characters: Record<string, { hero: { level: number } }> }, hero: HeroCharacter): number => p.characters[hero.classId]?.hero.level ?? 1;

/** Take a side quest from its giver. */
export function questAccept(profile: Profile, questId: string): FieldOp<{ questId: string }> {
  const def = sideQuestById(questId);
  if (!def) return fail('Unknown quest');
  const s = fieldOpStart(profile);
  if ('reason' in s) return s;
  const { p, hero, gain } = s;
  const st = sideQuestStatus(hero, def, levelOf(p, hero));
  if (st === 'active' || st === 'ready') return fail('You already have that quest');
  if (st === 'done') return fail('You have done that quest');
  if (st === 'hidden') return fail('That quest is not available yet');
  if (st === 'locked') return fail(`Requires level ${def.minLevel}`);
  const q = (hero.quests ??= emptySideQuests());
  if (Object.keys(q.active).length >= QUEST_LOG_MAX) return fail('Your quest log is full');
  if (!atQuestNpc(hero, def.giverId)) return fail('Too far away');
  q.active[def.id] = 0;
  fieldOpSettle(p, hero, gain);
  return { ok: true, profile: p, ...gain, questId: def.id };
}

/** Drop an active side quest (its progress is lost; it can be taken again). */
export function questAbandon(profile: Profile, questId: string): FieldOp<{ questId: string }> {
  const def = sideQuestById(questId);
  if (!def) return fail('Unknown quest');
  const s = fieldOpStart(profile);
  if ('reason' in s) return s;
  const { p, hero, gain } = s;
  const q = hero.quests;
  if (!q || q.active[def.id] === undefined) return fail('You do not have that quest');
  delete q.active[def.id];
  return { ok: true, profile: p, ...gain, questId: def.id };
}

const QUEST_SALT = 0x51de_9e57;

/** Hand in a finished side quest at its turn-in NPC: XP (scaled), gold, maybe gear. Rolls. */
export function questTurnIn(profile: Profile, questId: string): FieldOp<{ quest: SideQuestPaid; item: Item | null; equipped: boolean }> {
  const def: SideQuestDef | undefined = sideQuestById(questId);
  if (!def) return fail('Unknown quest');
  const s = fieldOpStart(profile);
  if ('reason' in s) return s;
  const { p, hero, gain } = s;
  const level = levelOf(p, hero);
  const st = sideQuestStatus(hero, def, level);
  if (st === 'done') return fail('You have done that quest');
  if (st !== 'ready') return fail(st === 'active' ? 'That quest is not finished yet' : 'You do not have that quest');
  if (!atQuestNpc(hero, questTurnInNpc(def))) return fail('Too far away');
  const q = hero.quests!;
  delete q.active[def.id];
  q.done[def.id] = (q.done[def.id] ?? 0) + 1;
  const xp = sideQuestXp(def.reward.xp, def.level, level);
  fieldOpGrant(p, hero, gain, xp, def.reward.gold);
  fieldOpSettle(p, hero, gain);
  // Gear: a piece for the hero's class at the quest's level (uncommon; profile seed, server-side online).
  let item: Item | null = null;
  let out = p;
  if (def.reward.gear) {
    const rng = profileRng(p, (QUEST_SALT ^ Math.imul(p.nextId, 0x9e3779b1) ^ def.id.length) >>> 0);
    const priority = CLASS_STAT_PROFILES[hero.classId]?.priority;
    const slot = rng.pick(ITEM_SLOTS) as ItemSlot;
    item = makeItem({ id: `i${p.nextId++}`, slot, ilvl: BASE_ILVL + 2 * def.level, rarity: 'uncommon', source: 'run', ...(priority ? { priority } : {}) }, rng);
    p.inventory.push(item);
    out = wearIfBetter(p, hero.classId, item.id);
  }
  const quest: SideQuestPaid = { id: def.id, title: def.title, xp: gain.xp, gold: def.reward.gold, grey: xp < def.reward.xp };
  return { ok: true, profile: out, ...gain, quest, item, equipped: out !== p };
}
